import type { ConfirmCalendarEmailResult, MeetingInviteSettings } from '@fos/shared/contracts/meetings';
import { useEffect, useState } from 'react';
import { errorMessage, validationIssues } from '../../../lib/api';
import { trpc } from '../../../lib/trpc';
import { Button } from '../../../ui/Button';
import { Checkbox } from '../../../ui/Checkbox';
import { CodeInput } from '../../../ui/CodeInput';
import { Field } from '../../../ui/Field';
import { Input } from '../../../ui/Input';
import { Section } from '../../../ui/Section';
import { Skeleton } from '../../../ui/States';
import { toast } from '../../../ui/Toast';
import { ResendButton } from '../../auth/components/StepUpCode';
import { TOUCH_BUTTON } from '../../mobile';

/**
 * Ustawienia → Profil → „Kalendarz” (DECISIONS 2026-10-09 [S] „Spotkania w Kalendarzu Google przez zaproszenie
 * mailem”): spotkania, które osoba dodaje w Fravity OS (także przez Copilota i Claude), przychodzą na jej adres jako
 * zaproszenie kalendarzowe z poczty systemowej. Domyślnie włączone, adres = email konta albo „Adres kalendarza”.
 * Nowy adres kalendarza potwierdza się kodem z maila; do tego czasu zaproszenia idą na email konta (spotkania tworzą
 * też Copilot i Claude, bez Akceptacji — treść spotkań nie może trafić na wpisany omyłkowo cudzy adres).
 */
export function CalendarInviteSection() {
  const utils = trpc.useUtils();
  const settings = trpc.meetings.inviteSettings.useQuery();
  const save = trpc.meetings.updateInviteSettings.useMutation({
    onSuccess: (data) => utils.meetings.inviteSettings.setData(undefined, data),
  });
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const saved = settings.data?.calendarEmail ?? '';

  useEffect(() => {
    setEmail(saved);
  }, [saved]);

  if (settings.isError) return null;
  const s = settings.data;
  const dirty = email.trim().toLowerCase() !== saved;

  function saveEmail() {
    setError(null);
    save.mutate(
      { calendarEmail: email.trim() },
      {
        onSuccess: (data) =>
          toast.success(
            !data.calendarEmail
              ? 'Zaproszenia na email konta'
              : data.calendarEmailVerified
                ? 'Zapisano adres kalendarza'
                : `Wysłaliśmy kod na ${data.calendarEmail}`,
          ),
        onError: (err) => setError(validationIssues(err)[0]?.message ?? errorMessage(err)),
      },
    );
  }

  return (
    <Section className="mt-6" title="Kalendarz">
      <p className="text-body-s text-fg-muted">
        Spotkania, które dodajesz w Fravity OS (także przez Copilota i Claude), przychodzą jako zaproszenie do Twojego
        kalendarza. Zmiana terminu aktualizuje wydarzenie, archiwizacja je odwołuje. Spotkania z kalendarza iCal i z
        przeszłości pomijamy.
      </p>
      {!s ? (
        <Skeleton className="mt-4 h-10 w-full md:max-w-sm" />
      ) : (
        <div className="mt-4 flex flex-col gap-4">
          <Checkbox
            label={<span className="font-medium text-fg">Wysyłaj zaproszenia do mojego kalendarza</span>}
            checked={s.enabled}
            disabled={save.isPending}
            onCheckedChange={(v) => save.mutate({ enabled: v }, { onError: (err) => toast.error(errorMessage(err)) })}
          />
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (dirty) saveEmail();
            }}
          >
            <Field
              label="Adres kalendarza"
              hint={`Np. Gmail. Puste = email konta (${s.accountEmail}).`}
              error={error}
              className="md:max-w-md"
            >
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={email}
                  placeholder={s.accountEmail}
                  disabled={!s.enabled || save.isPending}
                  autoComplete="email"
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError(null);
                  }}
                />
                <Button
                  type="submit"
                  variant="secondary"
                  disabled={!s.enabled || !dirty || save.isPending}
                  className={TOUCH_BUTTON}
                >
                  Zapisz
                </Button>
              </div>
            </Field>
          </form>
          {s.enabled && s.calendarEmail && !s.calendarEmailVerified && !dirty ? (
            <ConfirmCalendarEmail settings={s} />
          ) : null}
        </div>
      )}
    </Section>
  );
}

/** Kod z maila na nowy adres kalendarza; do potwierdzenia zaproszenia idą na email konta. */
function ConfirmCalendarEmail({ settings }: { settings: MeetingInviteSettings }) {
  const utils = trpc.useUtils();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const confirm = trpc.meetings.confirmCalendarEmail.useMutation();
  const resend = trpc.meetings.resendCalendarEmailCode.useMutation();
  const pending = confirm.isPending || resend.isPending;

  function submit(value: string) {
    if (!/^\d{6}$/.test(value) || pending) return;
    setError(null);
    confirm.mutate(
      { code: value },
      {
        onSuccess: (res) => {
          utils.meetings.inviteSettings.setData(undefined, res.settings);
          if (res.confirmed) {
            toast.success('Adres kalendarza potwierdzony');
            return;
          }
          setCode('');
          setError(CONFIRM_ERRORS[res.reason ?? 'wrong_code']);
        },
        onError: (err) => setError(errorMessage(err)),
      },
    );
  }

  return (
    <form
      className="flex flex-col gap-2 md:max-w-md"
      onSubmit={(e) => {
        e.preventDefault();
        submit(code);
      }}
    >
      <Field
        label="Kod z maila"
        error={error}
        hint={
          settings.pendingCode
            ? `Wysłaliśmy 6-cyfrowy kod na ${settings.calendarEmail}. Do potwierdzenia zaproszenia idą na ${settings.accountEmail}.`
            : `Adres czeka na potwierdzenie — do tego czasu zaproszenia idą na ${settings.accountEmail}. Wyślij nowy kod.`
        }
      >
        <CodeInput value={code} onChange={setCode} onComplete={submit} disabled={pending} />
      </Field>
      <div className="flex items-center gap-4">
        <Button type="submit" variant="secondary" disabled={pending || code.length !== 6} className={TOUCH_BUTTON}>
          Potwierdź
        </Button>
        <ResendButton
          availableAt={settings.pendingCode ? Date.parse(settings.pendingCode.resendAvailableAt) : null}
          pending={pending}
          onResend={() => {
            setError(null);
            resend.mutate(undefined, {
              onSuccess: (data) => {
                utils.meetings.inviteSettings.setData(undefined, data);
                setCode('');
                toast.success(`Wysłaliśmy nowy kod na ${data.calendarEmail}`);
              },
              onError: (err) => setError(errorMessage(err)),
            });
          }}
        />
      </div>
    </form>
  );
}

const CONFIRM_ERRORS: Record<NonNullable<ConfirmCalendarEmailResult['reason']>, string> = {
  wrong_code: 'Nieprawidłowy kod. Sprawdź mail i spróbuj ponownie.',
  expired: 'Kod wygasł. Wyślij nowy.',
  too_many_attempts: 'Za dużo prób. Wyślij nowy kod.',
  no_code: 'Brak ważnego kodu. Wyślij nowy.',
};
